import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Radio } from '@mui/material';
import { SearchQueryHint } from '@trinity/chat-components/components/presentation/Message.QueryHint';
import { SelectLocation } from '@trinity/chat-components/components/Search/Location';
import type { Chat } from '@trinity/chat';
import { ChatActionType } from '@trinity/chat';
import type { KolSearchResult } from '@trinity/chat';
import type { LocationItem } from '@trinity/chat';
import { useBrowserLocation } from '@trinity/chat-components/hooks/useBrowserLocation';
import { BrowserLocationError } from './BrowserLocationError';
import { SubmitActionContext } from './context';
import { SelectHcp } from './KolSearch.Form';

type Props = {
  queryId: number;
  queryIdentifier: string;
  value: Chat.QueryState.LocationSelection;
  hcp: KolSearchResult | null;
  disabled: boolean;
};

export const LocationForm = (props: Props) => {
  const submitAction = useContext(SubmitActionContext);
  const [radioState, setRadioState] = useState<'browser' | 'search' | 'hcp'>(props.hcp ? 'hcp' : props.value?.location?.type);
  const [selectorState, setSelectorState] = useState<LocationItem | null>(props.value?.location?.type === 'search' ? props.value.location : null);
  const [browserLocation, setBrowserLocation] = useState<ICoords | null>(props.value?.location?.type === 'browser' ? props.value.location : null);
  const [selectedHcp, setSelectedHcp] = useState<KolSearchResult | null>(props.hcp);
  const [hcpInputValue, setHcpInputValue] = useState<string>('');
  const [locationError, setLocationError] = useState<string | null>(null);
  const { isSupported, getLocation, state: browserLocationState } = useBrowserLocation({ onSuccess: setBrowserLocation, onError: setLocationError });

  useEffect(() => {
    if (radioState === 'browser' && !browserLocation) {
      getLocation();
    }
  }, [browserLocation, getLocation, radioState]);

  const onSubmit = useCallback(() => {
    if (radioState === 'browser' && browserLocation) {
      submitAction({
        type: ChatActionType.SelectLocation,
        payload: {
          location: {
            type: 'browser',
            latitude: browserLocation.latitude,
            longitude: browserLocation.longitude,
          },
          queryId: props.queryId,
        },
      }, 'Use current location');
    } else if (radioState === 'search' && selectorState) {
      submitAction({
        type: ChatActionType.SelectLocation,
        payload: {
          location: {
            type: 'search',
            name: selectorState.name,
            latitude: selectorState.latitude,
            longitude: selectorState.longitude,
            id: selectorState.id,
          },
          queryId: props.queryId,
        },
      }, `Use the following location: ${selectorState.name}`);
    } else if (radioState === 'hcp' && selectedHcp) {
      submitAction({
        type: ChatActionType.SelectLocation,
        payload: {
          location: {
            id: selectedHcp.id,
            type: 'hcp',
            name: selectedHcp.locationName,
            latitude: selectedHcp.locationCoords.latitude,
            longitude: selectedHcp.locationCoords.longitude,
          },
          queryId: props.queryId,
        },
      }, `Use the following HCP location: ${selectedHcp.name}`);
    }
  }, [browserLocation, props.queryId, radioState, selectedHcp, selectorState, submitAction]);

  const inputFocused = useRef(false);
  const handleHcpInputBlur = useCallback(() => {
    inputFocused.current = false;
  }, []);

  const handleHcpInputFocus = useCallback(() => {
    inputFocused.current = true;
  }, []);

  const canSubmit = useMemo(() => {
    if (props.disabled) return false;
    if (radioState === 'browser' && browserLocation) return true;

    if (radioState === 'search' && selectorState) return true;

    if (radioState === 'hcp' && selectedHcp) return true;

    return false;
  }, [browserLocation, props.disabled, radioState, selectedHcp, selectorState]);

  return (
    <div>
      <Copy>Welcome to the Smart Routing Assistant! I can help you plan your HCP site visits efficiently.</Copy>
      <Copy>First let's plan our where you'll be starting from.</Copy>
      <List>
        <RadioRow>
          <Radio size='small' disabled={!isSupported || props.disabled} checked={radioState === 'browser'} onClick={() => setRadioState('browser')} />
          <div>Use my current location</div>
          {browserLocationState === 'loading' && <div>Waiting for permission...</div>}
          {browserLocationState === 'success' && <div>{browserLocation.longitude}, {browserLocation.latitude}</div>}
          {browserLocationState === 'error' && <BrowserLocationError errorMessage={locationError} />}
        </RadioRow>
        <RadioRow>
          <Radio size='small' checked={radioState === 'search'} disabled={props.disabled} onClick={() => setRadioState('search')} />
          <RadioCopy>I'll be near this location</RadioCopy>
          <SelectLocation
            styles={{
              container: (provided) => ({
                ...provided,
                width: 350,
              }),
            }}
            placeholder="Start typing an address"
            isDisabled={radioState !== 'search' || props.disabled}
            value={selectorState}
            onChange={setSelectorState} />
        </RadioRow>
        <RadioRow>
          <Radio size='small' checked={radioState === 'hcp'} disabled={props.disabled} onClick={() => setRadioState('hcp')} />
          <RadioCopy>Use HCP's location</RadioCopy>
          <HcpSelectContainer>
            <SelectHcp
              disabled={props.disabled || radioState !== 'hcp' || !!props.hcp}
              formatOptionLabel={formatHcpOptionLabel}
              onInputFocus={handleHcpInputFocus}
              onInputBlur={handleHcpInputBlur}
              onInputChange={setHcpInputValue}
              onSelection={v => setSelectedHcp(v)}
              isClearable={true}
              value={selectedHcp}
              inputValue={hcpInputValue || ''} />
          </HcpSelectContainer>
        </RadioRow>
      </List>
      <StyledSearchHcps disabled={!canSubmit} onClick={onSubmit} value="Search HCPs" />
    </div>
  );
};

function formatHcpOptionLabel(result: KolSearchResult) {
  const specialty = result.specialties?.[0];
  return (
    <div>
      {result.name}{specialty ? <KolOptionSubText>({specialty})</KolOptionSubText> : null}
      {result.locationName ? <KolResultSecondaryText>{result.locationName}</KolResultSecondaryText> : null}
    </div>
  );
}

const KolResultSecondaryText = styled.div(({ theme }) => ({
  color: theme.palette.gray.main,
  marginTop: 5,
  fontSize: 15,
}));
const KolOptionSubText = styled.span(({ theme }) => ({
  fontSize: 14,
  marginLeft: 5,
  color: theme.palette.gray.main,
}));


const RadioCopy = styled.div(({
  width: 165,
}));

const Copy = styled.div({
  marginBottom: 5,
});

const StyledSearchHcps = styled(SearchQueryHint)({
  marginTop: 10,
});

const List = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
});

const RadioRow = styled.div(() => ({
  display: 'flex',
  gap: 10,
  alignItems: 'center',
}));

const HcpSelectContainer = styled.div({
  width: 350,
});